/**
 * Catálogo para otorgar acceso a reportes (usuario o grupo).
 * Cliente → proyecto, búsqueda, filtros y asignación por lote.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink, studio, SearchField, FilterDock } from './studioUi';

const ALL = '__all__';

function clientNameOf(report) {
    return report.cliente || 'Sin cliente';
}

function projectNameOf(report) {
    return report.proyecto || 'Sin proyecto';
}

function matchesQuery(report, query) {
    if (!query) return true;
    const hay = `${report.reporteNombre || ''} ${clientNameOf(report)} ${projectNameOf(report)}`.toLowerCase();
    return hay.includes(query);
}

function AccessSwitch({ on, disabled, pending, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            disabled={disabled || pending}
            onClick={() => onChange(!on)}
            title={on ? 'Quitar acceso' : 'Dar acceso'}
            style={{
                width: 40,
                height: 22,
                borderRadius: 999,
                border: 'none',
                backgroundColor: on ? ink.accent : ink.lineStrong,
                position: 'relative',
                cursor: disabled || pending ? 'not-allowed' : 'pointer',
                opacity: pending ? 0.55 : 1,
                padding: 0,
                flexShrink: 0,
            }}
        >
            <span
                style={{
                    position: 'absolute',
                    top: 3,
                    left: on ? 20 : 3,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    transition: 'left 0.15s ease',
                    display: 'block',
                }}
            />
        </button>
    );
}

function AccessTypePills({ value, onChange, disabled }) {
    const opts = [
        { id: 'view', label: 'Vista' },
        { id: 'edit', label: 'Edición' },
    ];
    return (
        <div style={{
            display: 'inline-flex',
            backgroundColor: ink.wash,
            borderRadius: 8,
            padding: 2,
            border: `1px solid ${ink.line}`,
        }}>
            {opts.map((opt) => {
                const active = (value || 'view') === opt.id;
                return (
                    <button
                        key={opt.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => !active && onChange(opt.id)}
                        style={{
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            backgroundColor: active ? ink.surface : 'transparent',
                            color: active ? ink.accentText : ink.muted,
                            boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                        }}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

function ScopeButton({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                appearance: 'none',
                border: 'none',
                borderRadius: 8,
                padding: '7px 12px',
                fontSize: '0.82rem',
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: 'pointer',
                backgroundColor: active ? ink.surface : 'transparent',
                color: active ? ink.accentText : ink.muted,
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
        >
            {children}
        </button>
    );
}

function NavRow({ active, onClick, title, meta, nested }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                width: '100%',
                textAlign: 'left',
                appearance: 'none',
                border: 'none',
                borderRadius: 10,
                padding: nested ? '8px 10px 8px 18px' : '9px 10px',
                marginBottom: 2,
                cursor: 'pointer',
                fontFamily: 'inherit',
                backgroundColor: active ? ink.accentSoft : 'transparent',
                color: active ? ink.accentText : ink.text,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
            }}
        >
            <span style={{
                fontSize: nested ? '0.82rem' : '0.88rem',
                fontWeight: active ? 600 : 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {title}
            </span>
            <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: active ? ink.accentText : ink.faint,
                flexShrink: 0,
            }}>
                {meta}
            </span>
        </button>
    );
}

const ReportAccessPicker = ({
    reports = [],
    getPermission,
    onToggle,
    onAccessTypeChange,
    onBulkToggle,
    canAssign = true,
    canRevoke = true,
    canChangeType = true,
    busy = false,
}) => {
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState('all');
    const [clientKey, setClientKey] = useState(ALL);
    const [projectKey, setProjectKey] = useState(ALL);
    const [pendingKey, setPendingKey] = useState(null);

    const query = search.trim().toLowerCase();

    const catalog = reports || [];
    const grantedTotal = catalog.filter((report) => !!getPermission(report)).length;

    const visible = useMemo(() => catalog.filter((report) => {
        if (!matchesQuery(report, query)) return false;
        const granted = !!getPermission(report);
        if (scope === 'granted' && !granted) return false;
        if (scope === 'missing' && granted) return false;
        return true;
    }), [catalog, query, scope, getPermission]);

    const tree = useMemo(() => {
        const byClient = new Map();
        visible.forEach((report) => {
            const client = clientNameOf(report);
            if (!byClient.has(client)) byClient.set(client, new Map());
            const projects = byClient.get(client);
            const project = projectNameOf(report);
            if (!projects.has(project)) projects.set(project, []);
            projects.get(project).push(report);
        });
        return Array.from(byClient.entries())
            .sort((a, b) => a[0].localeCompare(b[0], 'es'))
            .map(([name, projects]) => {
                const projectList = Array.from(projects.entries())
                    .sort((a, b) => a[0].localeCompare(b[0], 'es'))
                    .map(([projectName, list]) => ({
                        name: projectName,
                        reports: list,
                        granted: list.filter((item) => !!getPermission(item)).length,
                    }));
                const allReports = projectList.flatMap((item) => item.reports);
                return {
                    name,
                    projects: projectList,
                    reports: allReports,
                    granted: allReports.filter((item) => !!getPermission(item)).length,
                };
            });
    }, [visible, getPermission]);

    useEffect(() => {
        if (clientKey === ALL) return;
        if (!tree.some((client) => client.name === clientKey)) {
            setClientKey(ALL);
            setProjectKey(ALL);
        }
    }, [tree, clientKey]);

    const selectedClient = tree.find((client) => client.name === clientKey) || null;
    const selectedProject = selectedClient?.projects.find((project) => project.name === projectKey) || null;

    const groups = useMemo(() => {
        if (selectedClient && selectedProject) {
            return [{ client: selectedClient.name, project: selectedProject.name, reports: selectedProject.reports, granted: selectedProject.granted }];
        }
        if (selectedClient) {
            return selectedClient.projects.map((project) => ({
                client: selectedClient.name,
                project: project.name,
                reports: project.reports,
                granted: project.granted,
            }));
        }
        return tree.flatMap((client) => client.projects.map((project) => ({
            client: client.name,
            project: project.name,
            reports: project.reports,
            granted: project.granted,
        })));
    }, [tree, selectedClient, selectedProject]);

    const listedReports = groups.flatMap((group) => group.reports);
    const listedGranted = listedReports.filter((report) => !!getPermission(report)).length;
    const locked = busy || !!pendingKey;

    const runToggle = async (report, grant) => {
        if (grant && !canAssign) return;
        if (!grant && !canRevoke) return;
        setPendingKey(report.id);
        try {
            await onToggle(report, grant);
        } finally {
            setPendingKey(null);
        }
    };

    const runBulk = async (list, grant) => {
        const targets = list.filter((report) => grant ? !getPermission(report) : !!getPermission(report));
        if (!targets.length) return;
        if (grant && !canAssign) return;
        if (!grant && !canRevoke) return;
        if (grant && targets.length > 5 && !window.confirm(`Vas a dar acceso a ${targets.length} reportes. Se enviará una notificación por cada uno. ¿Continuar?`)) {
            return;
        }
        if (!grant && !window.confirm(`¿Quitar el acceso a ${targets.length} reportes?`)) {
            return;
        }
        setPendingKey('bulk');
        try {
            if (onBulkToggle) {
                await onBulkToggle(targets, grant);
            } else {
                for (const report of targets) {
                    await onToggle(report, grant);
                }
            }
        } finally {
            setPendingKey(null);
        }
    };

    const runAccessType = async (permission, nextType) => {
        if (!canChangeType || !permission) return;
        setPendingKey(permission.id);
        try {
            await onAccessTypeChange(permission.id, nextType);
        } finally {
            setPendingKey(null);
        }
    };

    return (
        <div>
            <FilterDock style={{ marginBottom: 14 }}>
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Nombre, cliente o proyecto…"
                    style={{ marginBottom: 10 }}
                />
                <div>
                    <span style={studio.fieldLabel}>Mostrar</span>
                    <div style={studio.scopeBar}>
                        <ScopeButton active={scope === 'all'} onClick={() => setScope('all')}>Todos</ScopeButton>
                        <ScopeButton active={scope === 'granted'} onClick={() => setScope('granted')}>Con acceso</ScopeButton>
                        <ScopeButton active={scope === 'missing'} onClick={() => setScope('missing')}>Sin acceso</ScopeButton>
                    </div>
                </div>
            </FilterDock>

            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                marginBottom: 12,
                flexWrap: 'wrap',
            }}>
                <p style={{ ...studio.hint, margin: 0 }}>
                    {grantedTotal} de {catalog.length} reportes con acceso
                    {listedReports.length !== catalog.length ? ` · ${listedGranted}/${listedReports.length} en esta vista` : ''}
                </p>
                {listedReports.length > 0 && (canAssign || canRevoke) && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        {canAssign && listedGranted < listedReports.length && (
                            <button type="button" disabled={locked} onClick={() => runBulk(listedReports, true)} style={{ ...studio.ghostBtn, padding: '7px 12px', fontSize: '0.8rem' }}>
                                Dar visibles
                            </button>
                        )}
                        {canRevoke && listedGranted > 0 && (
                            <button type="button" disabled={locked} onClick={() => runBulk(listedReports, false)} style={{ ...studio.dangerBtn, padding: '7px 12px', fontSize: '0.8rem' }}>
                                Quitar visibles
                            </button>
                        )}
                    </div>
                )}
            </div>

            {catalog.length === 0 && (
                <div style={studio.empty}>No hay reportes en la biblioteca.</div>
            )}

            {catalog.length > 0 && (
                <div style={studio.split}>
                    <aside style={studio.splitNav}>
                        <NavRow
                            active={clientKey === ALL}
                            onClick={() => { setClientKey(ALL); setProjectKey(ALL); }}
                            title="Todos los clientes"
                            meta={`${grantedTotal}/${catalog.length}`}
                        />
                        {tree.map((client) => (
                            <div key={client.name}>
                                <NavRow
                                    active={clientKey === client.name && projectKey === ALL}
                                    onClick={() => { setClientKey(client.name); setProjectKey(ALL); }}
                                    title={client.name}
                                    meta={`${client.granted}/${client.reports.length}`}
                                />
                                {clientKey === client.name && client.projects.map((project) => (
                                    <NavRow
                                        key={project.name}
                                        nested
                                        active={projectKey === project.name}
                                        onClick={() => { setClientKey(client.name); setProjectKey(project.name); }}
                                        title={project.name}
                                        meta={`${project.granted}/${project.reports.length}`}
                                    />
                                ))}
                            </div>
                        ))}
                    </aside>
                    <div style={studio.splitMain}>
                        {pendingKey === 'bulk' && (
                            <p style={{ ...studio.hint, padding: '8px 18px' }}>Aplicando cambios…</p>
                        )}
                        {groups.length === 0 && (
                            <div style={studio.empty}>Ningún reporte coincide con la búsqueda o el filtro.</div>
                        )}
                        {groups.map((group) => (
                            <section key={`${group.client}-${group.project}`} style={{ padding: '6px 8px 14px' }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '8px 10px',
                                    margin: '0 8px 4px',
                                    borderRadius: 10,
                                    backgroundColor: ink.wash,
                                }}>
                                    <div>
                                        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: ink.faint }}>
                                            {group.client}
                                        </div>
                                        <div style={{ fontWeight: 600, color: ink.text, fontSize: '0.95rem' }}>{group.project}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                        {canAssign && group.granted < group.reports.length && (
                                            <button type="button" disabled={locked} onClick={() => runBulk(group.reports, true)} style={{ ...studio.ghostBtn, padding: '6px 10px', fontSize: '0.75rem' }}>
                                                Dar todos
                                            </button>
                                        )}
                                        {canRevoke && group.granted > 0 && (
                                            <button type="button" disabled={locked} onClick={() => runBulk(group.reports, false)} style={{ ...studio.dangerBtn, padding: '6px 10px', fontSize: '0.75rem' }}>
                                                Quitar
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {group.reports.map((report) => {
                                    const permission = getPermission(report);
                                    const granted = !!permission;
                                    const rowPending = pendingKey === report.id || (permission && pendingKey === permission.id);
                                    return (
                                        <div
                                            key={report.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12,
                                                padding: '10px 18px',
                                                borderBottom: `1px solid ${ink.line}`,
                                                backgroundColor: granted ? 'rgba(94, 53, 177, 0.04)' : 'transparent',
                                                opacity: rowPending ? 0.7 : 1,
                                            }}
                                        >
                                            <AccessSwitch
                                                on={granted}
                                                pending={rowPending || pendingKey === 'bulk'}
                                                disabled={(locked && !rowPending) || (granted ? !canRevoke : !canAssign)}
                                                onChange={(next) => runToggle(report, next)}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, color: ink.text, fontSize: '0.92rem' }}>{report.reporteNombre}</div>
                                                {clientKey === ALL && (
                                                    <div style={{ fontSize: '0.78rem', color: ink.faint, marginTop: 2 }}>
                                                        {clientNameOf(report)} · {projectNameOf(report)}
                                                    </div>
                                                )}
                                            </div>
                                            {granted ? (
                                                <AccessTypePills
                                                    value={permission.accessType || 'view'}
                                                    disabled={!canChangeType || locked}
                                                    onChange={(next) => runAccessType(permission, next)}
                                                />
                                            ) : (
                                                <span style={{ fontSize: '0.75rem', color: ink.faint, fontWeight: 600 }}>Sin acceso</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </section>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportAccessPicker;
